import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ResellerListRow } from '@domain/reseller/Reseller';
import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { HttpResellerRepository } from '@infrastructure/api/HttpResellerRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { CreateResellerModal } from './CreateResellerModal';
import { StatusBadge } from '@app/components/StatusBadge';

const repo = new HttpResellerRepository(apiClient);

interface ResellerKPIs {
  total_resellers: number;
  active_resellers: number;
  pending_resellers: number;
  suspended_resellers: number;
  active_sessions: number;
  today_messages: number;
  today_revenue: string;
  month_revenue: string;
  total_balance: string;
  low_balance_count: number;
}

// A missing stat shows "—", never "undefined" / "$undefined"
const n = (v: number | string | null | undefined) => (v === null || v === undefined ? '—' : v);
const usd = (v: number | string | null | undefined) => (v === null || v === undefined ? '—' : `$${v}`);

function KpiBar({ data }: { data: ResellerKPIs | null }) {
  if (!data) return null;
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
      <Card title="Total" value={n(data.total_resellers)} />
      <Card title="Active" value={n(data.active_resellers)} sub={`${n(data.pending_resellers)} pending · ${n(data.suspended_resellers)} suspended`} />
      <Card title="Live SMPP sessions" value={n(data.active_sessions)} />
      <Card title="Today" value={n(data.today_messages)} sub={`${usd(data.today_revenue)} revenue`} />
      <Card title="Total balance" value={usd(data.total_balance)} sub={`${n(data.low_balance_count)} low-balance`} />
    </div>
  );
}

function Card({ title, value, sub }: { title: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border bg-white p-3 shadow-sm">
      <div className="text-xs uppercase text-slate-500">{title}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function ResellersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [kpis, setKpis] = useState<ResellerKPIs | null>(null);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/dashboard/`)
      .then((r) => setKpis(r.data))
      .catch(() => setKpis(null));
  }, []);

  const filters = useMemo<{ search?: string; status?: string }>(() => {
    const f: { search?: string; status?: string } = {};
    if (search) f.search = search;
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [search, statusFilter]);

  const columns: DataTableColumn<ResellerListRow>[] = [
    { id: 'name', header: 'Name', accessor: 'name' },
    { id: 'system_id', header: 'System ID', accessor: (r) => <code className="font-mono text-xs">{r.system_id}</code> },
    { id: 'tier_level', header: 'Tier', accessor: 'tier_level' },
    { id: 'billing_model', header: 'Billing', accessor: 'billing_model' },
    { id: 'balance', header: 'Balance', accessor: (r) => `$${r.balance}` },
    { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
    { id: 'created_at', header: 'Created', accessor: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">SMPP Resellers</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="search"
            placeholder="Search name or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56 rounded border px-2 py-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
          <button type="button" onClick={() => setShowCreate(true)} className="rounded bg-blue-600 px-3 py-1 text-white">
            New reseller
          </button>
        </div>
      </div>

      <CreateResellerModal
        open={showCreate}
        repo={repo}
        onClose={() => setShowCreate(false)}
        onOpenReseller={(id) => { setShowCreate(false); navigate(`/resellers/${id}`); }}
      />

      <KpiBar data={kpis} />

      <DataTable<ResellerListRow>
        columns={columns}
        fetcher={(p) => repo.list({ ...p, ...filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/resellers/${r.id}`)}
        emptyState={<span data-testid="datatable-empty">No SMPP resellers yet.</span>}
      />
    </section>
  );
}
